import {
  Button,
  Divider,
  Grid,
  Typography,
  Box,
  MenuItem,
  ListItemIcon,
  Autocomplete,
  TextField,
  FormGroup,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ServerMessageSnackbar from "../../../reusableComponents/serverMessageSnackbar/serverMessageSnackbar";
import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import { useNavigate } from "react-router-dom";
import Spinner from "../../../reusableComponents/spinner/spinner";
import { getUserClients } from "../../../../store/thunks/portalManagementThunk";
import { logoutUser } from "../../../../store/thunks/authenticationThunk";
import { portalManagementReset } from "../../../../store/features/portalManagement/portalManagementSlice";
import { systemManagementReset } from "../../../../store/features/systemManagement/systemManagementSlice";
import { logout } from "../../../../store/features/authentication/authenticationSlice";
import TableTemplate from "../../../reusableComponents/tableTemplate/tableTemplate";
import { filterAutofillGuard } from "../../../../utils/tableUtils";
import { MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  activateSalesRep,
  deactivateSalesRep,
  getAllClientSalesmen,
  getClientAdmin,
  updateClientAdminNotification,
  updateClientNotification,
} from "../../../../store/thunks/systemManagementThunk";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UnsubscribeIcon from "@mui/icons-material/Unsubscribe";
import LockResetIcon from "@mui/icons-material/LockReset";
import EnableSalesRepDialog from "./enableSalesRepDialog/enableSalesRepDialog";

const SalesRepManagement = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state: any) => state.authentication);
  const { clientsLoading, clients } = useAppSelector(
    (state: any) => state.portalManagement
  );
  const {
    getAllClientSalesmenLoading,
    allClientSalesMen,
    activateSalesRepLoading,
    deactivateSalesRepLoading,
    clientAdmin,
  } = useAppSelector((state: any) => state.systemManagement);
  const [selectedClientSalerep, setSelectedClientSalerep] = useState<any>(null);
  const [selectedSalesRepData, setSelectedSalesRepData] = useState<any>(null);
  const [clientSalesRepNotification, setClientSalesRepNotification] =
    useState<boolean>(false);
  const [selectedSalesRepNavigation, setSelectedSalesRepNavigation] =
    useState<string>("active_sales_rep");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [openEnableSalesRepDialog, setOpenEnableSalesRepDialog] = useState(false);

  const handleSelectClientSalesRep = async (newValue: any) => {
    setSelectedClientSalerep(newValue);
    setClientSalesRepNotification(
      newValue?.sales_rep_notification === 0 ? false : true
    );

    if (newValue) {
      await dispatch(
        getAllClientSalesmen({
          customer_id: newValue?.customer_id,
        })
      ).then((getAllClientSalesmenResult: any) => {
        if (getAllClientSalesmenResult?.error) {
          if (getAllClientSalesmenResult?.error?.message === "Rejected") {
            dispatch(logoutUser({ user_index: user?.client_user_index }));
            dispatch(portalManagementReset());
            dispatch(systemManagementReset());
            dispatch(logout());
            navigate("/login");
          }
        }
      });
    }
  };

  useEffect(() => {
    dispatch(getUserClients({})).then((getClientsInfoResult: any) => {
      if (getClientsInfoResult?.error) {
        if (getClientsInfoResult?.error?.message === "Rejected") {
          dispatch(logoutUser({ user_index: user?.client_user_index }));
          dispatch(portalManagementReset());
          dispatch(systemManagementReset());
          dispatch(logout());
          navigate("/login");
        }
      }
    });
  }, []);

  const {
    data: { data = [] } = {}, //your data and api response will probably be different
    isError,
    isRefetching,
    isLoading,
    refetch,
  } = useQuery<any>({
    // The customer and the tab are part of the key, so switching either
    // refetches on its own. An effect used to do that alongside this query, and
    // both fired on mount with a customer_id that does not exist yet — two
    // requests to /all_client_salesmen/undefined every time the screen opened.
    queryKey: [
      "client-salesmen",
      selectedClientSalerep?.customer_id,
      selectedSalesRepNavigation,
    ],
    enabled: Boolean(selectedClientSalerep?.customer_id),
    queryFn: async () => {
      const customerId = selectedClientSalerep?.customer_id;
      if (selectedSalesRepNavigation === "active_sales_rep") {
        await dispatch(getAllClientSalesmen({ customer_id: customerId }));
      } else {
        await dispatch(getClientAdmin({ client_id: customerId }));
      }
      return {};
    },
    placeholderData: keepPreviousData, //don't go to 0 rows when refetching or paginating to next page
  });

  const salerepColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        id: "Table",
        header: "",
        columns: [
          {
            accessorKey: "SALESMAN", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Sale Rep",
          },
          {
            accessorFn: (row: any) =>
              `${row.first_name ? row.first_name : ""} ${
                row.last_name ? row.last_name : ""
              }`, //accessorFn used to join multiple data into a single cell
            id: "name", //id is still required when using accessorFn instead of accessorKey
            header: "Full Name",
            Cell: ({ renderedCellValue, row }) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <span>{renderedCellValue}</span>
              </Box>
            ),
          },
          {
            accessorKey: "user_name",
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Email",
          },
          {
            accessorKey: "status",
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Sale Rep Status",
          },
        ],
      },
    ],
    []
  );

  const salerepTable = useMaterialReactTable({
    muiFilterTextFieldProps: filterAutofillGuard,
    columns: salerepColumns,
    data: allClientSalesMen ? allClientSalesMen : [], //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    state: {
      isLoading: getAllClientSalesmenLoading, //cell skeletons and loading overlay
      // showProgressBars: getUserLoading, //progress bars while refetching
      // isSaving: getUserLoading, //progress bars and save button spinners
    },
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageIndex: 0, pageSize: 10 },
      columnPinning: {
        left: ["mrt-row-expand", "mrt-row-select"],
        right: ["mrt-row-actions"],
      },
    },
    positionToolbarAlertBanner: "bottom",
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      !row.original?.user_name && (
        <MenuItem
          key={2}
          onClick={() => {
            setSelectedSalesRepData(row.original);
            setOpenEnableSalesRepDialog(true);
            closeMenu();
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          Assign Sales Rep
        </MenuItem>
      ),
      row.original?.status === "Active" && (
        <MenuItem
          key={2}
          onClick={() => {
            handleDeactivateSalesRep(row.original);
            closeMenu();
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          Disable Sales Rep
        </MenuItem>
      ),
      row.original?.status === "Inactive" && (
        <MenuItem
          key={2}
          onClick={() => {
            handleActivateSalesRep(row.original);
            closeMenu();
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          Activate Sales Rep
        </MenuItem>
      ),
    ],
  });

  const clientAdminColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        id: "Table",
        header: "",
        columns: [
          {
            accessorKey: "user_name", //accessorKey used to define `data` column. `id` gets set to accessorKey automatically
            enableClickToCopy: true,
            filterVariant: "autocomplete",
            header: "Client Admin Email",
          },
          {
            accessorFn: (row: any) =>
              `${row.first_name ? row.first_name : ""} ${
                row.last_name ? row.last_name : ""
              }`, //accessorFn used to join multiple data into a single cell
            id: "name", //id is still required when using accessorFn instead of accessorKey
            header: "Full Name",
            Cell: ({ renderedCellValue, row }) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <span>{renderedCellValue}</span>
              </Box>
            ),
          },

          {
            accessorKey: "recieve_sales_rep_notification",
            enableClickToCopy: true,
            // filterVariant: "autocomplete",
            header: "Receive Sales Rep Notification",
            Cell: ({ renderedCellValue, row }) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <span>{renderedCellValue === 0 ? "No" : "Yes"}</span>
              </Box>
            ),
          },
        ],
      },
    ],
    []
  );

  const clientAdminTable = useMaterialReactTable({
    muiFilterTextFieldProps: filterAutofillGuard,
    columns: clientAdminColumns,
    data: clientAdmin ? clientAdmin : [], //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    state: {
      isLoading: getAllClientSalesmenLoading, //cell skeletons and loading overlay
      // showProgressBars: getUserLoading, //progress bars while refetching
      // isSaving: getUserLoading, //progress bars and save button spinners
    },
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageIndex: 0, pageSize: 10 },
      columnPinning: {
        left: ["mrt-row-expand", "mrt-row-select"],
        right: ["mrt-row-actions"],
      },
    },
    positionToolbarAlertBanner: "bottom",
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      row.original?.recieve_sales_rep_notification === 0 && (
        <MenuItem
          key={2}
          onClick={() => {
            handleActivateClientAdminNotification(row.original);
            closeMenu();
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          Activate Client Admin Notification
        </MenuItem>
      ),
      row.original?.recieve_sales_rep_notification === 1 && (
        <MenuItem
          key={2}
          onClick={() => {
            handleDisableClientAdminNotification(row.original);
            closeMenu();
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          Disable Client Admin Notification
        </MenuItem>
      ),
    ],
  });

  const handleCloseEnableSalesRepDialog = (message: string) => {
    if (message !== "") {
      setSnackbarMessage(message);
      setOpenSnackbar(true);
      refetch();
    }
    setOpenEnableSalesRepDialog(false);
  };

  const handleDeactivateSalesRep = (salesRepData: any) => {
    dispatch(
      deactivateSalesRep({
        deactivateSalesRepPayload: {
          client_user_index: salesRepData?.client_user_index
            ? salesRepData?.client_user_index
            : "",
          sales_rep_name: salesRepData?.SALESMAN,
          customer_id: salesRepData?.customer_id,
        },
      })
    ).then((deactivateSalesRepResult: any) => {
      setSnackbarMessage(deactivateSalesRepResult?.payload?.message);
      setOpenSnackbar(true);
      refetch();
    });
  };

  const handleActivateSalesRep = (salesRepData: any) => {
    dispatch(
      activateSalesRep({
        activateSalesRepPayload: {
          client_user_index: salesRepData?.client_user_index
            ? salesRepData?.client_user_index
            : "",
          sales_rep_name: salesRepData?.SALESMAN,
          customer_id: salesRepData?.customer_id,
        },
      })
    ).then((activateSalesRepResult: any) => {
      setSnackbarMessage(activateSalesRepResult?.payload?.message);
      setOpenSnackbar(true);
      refetch();
    });
  };

  const handleUpdateClientSalesRepNotification = (notification: boolean) => {
    setClientSalesRepNotification(notification);
    dispatch(
      updateClientNotification({
        client_id: selectedClientSalerep?.customer_id,
        notification,
      })
    ).then((updateClientNotificationResult: any) => {
      setSnackbarMessage(updateClientNotificationResult?.payload?.message);
      setOpenSnackbar(true);
      dispatch(getUserClients({}));
    });
  };

  const handleActivateClientAdminNotification = (clientAdminData: any) => {
    dispatch(
      updateClientAdminNotification({
        client_index: clientAdminData?.client_index,
        client_user_index: clientAdminData?.client_user_index,
        notification: 1,
      })
    )
      .then((updateClientAdminNotificationResult: any) => {
        setSnackbarMessage(
          updateClientAdminNotificationResult?.payload?.message
        );
        setOpenSnackbar(true);
        dispatch(
          getClientAdmin({ client_id: selectedClientSalerep?.customer_id })
        );
      })
      .catch((error: any) => {
        setSnackbarMessage(error?.payload?.message);
        setOpenSnackbar(true);
      });
  };

  const handleDisableClientAdminNotification = (clientAdminData: any) => {
    dispatch(
      updateClientAdminNotification({
        client_index: clientAdminData?.client_index,
        client_user_index: clientAdminData?.client_user_index,
        notification: 0,
      })
    )
      .then((updateClientAdminNotificationResult: any) => {
        setSnackbarMessage(
          updateClientAdminNotificationResult?.payload?.message
        );
        setOpenSnackbar(true);
        dispatch(
          getClientAdmin({ client_id: selectedClientSalerep?.customer_id })
        );
        // refetch();
      })
      .catch((error: any) => {
        setSnackbarMessage(error?.payload?.message);
        setOpenSnackbar(true);
      });
  };

  return (
    <Grid>
      <Spinner loading={clientsLoading} />
      <Spinner loading={activateSalesRepLoading} />
      <Spinner loading={deactivateSalesRepLoading} />
      <Grid
        container
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 20px 10px 20px",
        }}
      >
        <Typography variant="h4" sx={{ color: "#004680", fontWeight: "bold" }}>
          Sales Rep Management
        </Typography>
      </Grid>
      <Grid
        container
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0px 20px 10px 20px !important",
        }}
      >
        <Grid item xs={6}>
          <Autocomplete
            id="clients"
            value={selectedClientSalerep}
            onChange={(_: SyntheticEvent, newValue: string | null) => {
              handleSelectClientSalesRep(newValue);
            }}
            options={clients}
            getOptionLabel={(option) => option.customer_name}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Clients"
                label="Select Client"
              />
            )}
          />
        </Grid>
        {selectedClientSalerep && (
          <Grid container>
            <Grid container item xs={12} sx={{ padding: "20px 0px 20px 0px" }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={clientSalesRepNotification}
                      onChange={(e) => {
                        handleUpdateClientSalesRepNotification(
                          e.target.checked
                        );
                      }}
                    />
                  }
                  label="Enable/Disable Client Sales Rep Notification"
                />
              </FormGroup>
            </Grid>
            <Grid container>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  backgroundColor: "#4C4C4C !important",
                  borderTop: "5px solid white !important",
                  height: "40px",
                  paddingLeft: "10px",
                }}
              >
                <Button
                  onClick={() => {
                    setSelectedSalesRepNavigation("active_sales_rep");
                    // refetch();
                  }}
                  sx={{
                    color:
                      selectedSalesRepNavigation === "active_sales_rep"
                        ? "#38B54A !important"
                        : "white !important",
                    padding: "0px 20px 0px 20px !important",
                  }}
                >
                  Active Sales Reps
                </Button>
                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ color: "white", backgroundColor: "white" }}
                />
                <Button
                  onClick={() => {
                    setSelectedSalesRepNavigation("client_admin_notification");
                    // refetch();
                  }}
                  sx={{
                    color:
                      selectedSalesRepNavigation === "client_admin_notification"
                        ? "#38B54A !important"
                        : "white !important",
                    padding: "0px 20px 0px 20px !important",
                  }}
                >
                  Client Admin Notification
                </Button>
              </Grid>
              {selectedSalesRepNavigation === "active_sales_rep" ? (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{ padding: "20px 0px 20px 0px" }}
                >
                  <TableTemplate tableData={salerepTable} />
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{ padding: "20px 0px 20px 0px" }}
                >
                  <TableTemplate tableData={clientAdminTable} />
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
      <EnableSalesRepDialog
        open={openEnableSalesRepDialog}
        handleClose={handleCloseEnableSalesRepDialog}
        selectedSalesRepData={selectedSalesRepData}
      />
      <ServerMessageSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        onClose={() => setOpenSnackbar(false)}
      />
    </Grid>
  );
};
export default SalesRepManagement;
